import { useEffect, useRef, useState } from 'react'
import { Box, Button, HStack, Input, Text, VStack } from '@chakra-ui/react'
import { businessApi } from '../../../lib/api'
import type { BusinessSuggest } from '../../../types/business'

/** Exactly one of the two is ever set. Sending both would AND two employment predicates
 *  server-side and silently return nothing. */
export interface EmployerSelection {
  businessId?: string
  employerName?: string
}

interface EmployerFilterProps {
  value: EmployerSelection
  onChange: (next: EmployerSelection) => void
  placeholder?: string
}

const MIN_QUERY = 2

const sameSelection = (a: EmployerSelection, b: EmployerSelection) =>
  (a.businessId ?? undefined) === (b.businessId ?? undefined)
  && (a.employerName ?? undefined) === (b.employerName ?? undefined)

/** Employer picker for both recruiting paths. Prefers a linked Business (exact id, immune to
 *  the free-text mismatch that plagues districts), but accepts arbitrary text because most
 *  member lists arrive from locals with employers keyed rather than linked. */
export function EmployerFilter({
  value, onChange, placeholder = 'Employer (e.g. Green Leaf Cannabis)',
}: EmployerFilterProps) {
  const [text, setText] = useState(value.employerName ?? '')
  const [options, setOptions] = useState<BusinessSuggest[]>([])
  const [picked, setPicked] = useState<BusinessSuggest | null>(null)
  const latest = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // The selection we last told the parent about. Lets the resync effect below tell "the parent
  // simply echoed back what we just emitted" apart from "the parent changed `value` on its own" —
  // without that distinction, resyncing on every prop change wipes the label right after a pick.
  const lastEmittedRef = useRef<EmployerSelection>(value)
  // True only while the user is actively typing a keystroke. Gates the lookup so a `value`
  // hydrated from props (on mount, or resynced from a genuine external change) never fires an
  // unwanted query — only real user input should.
  const typingRef = useRef(false)

  // Controlled resync: reflect a genuine external `value` change into local state, but ignore
  // the parent merely echoing back the selection we ourselves just emitted (see pick/commit/clear).
  useEffect(() => {
    if (sameSelection(value, lastEmittedRef.current)) return
    lastEmittedRef.current = value
    typingRef.current = false
    setText(value.employerName ?? '')
    setOptions([])
    setPicked((prev) => (value.businessId && prev?.id === value.businessId ? prev : null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.businessId, value.employerName])

  useEffect(() => {
    if (!typingRef.current || picked || text.trim().length < MIN_QUERY) { setOptions([]); return }
    const seq = ++latest.current
    let cancelled = false
    timerRef.current = setTimeout(async () => {
      try {
        const res = await businessApi.suggest(text.trim(), 8)
        // Ignore a response that a later keystroke has already superseded.
        if (!cancelled && seq === latest.current) setOptions(res.data)
      } catch {
        if (!cancelled && seq === latest.current) setOptions([])
      }
    }, 250)
    return () => { cancelled = true; clearTimeout(timerRef.current) }
  }, [text, picked])

  // Invalidate any in-flight or pending lookup. Needed on commit specifically: picking or
  // clearing already change `text`/`picked`, which tears down the lookup effect's own timer as
  // a side effect — but committing free text changes neither, so without this the debounced
  // fetch would still land after the user considers the interaction finished, popping the
  // dropdown back open.
  const cancelPendingLookup = () => {
    latest.current += 1
    clearTimeout(timerRef.current)
  }

  const emit = (next: EmployerSelection) => {
    lastEmittedRef.current = next
    onChange(next)
  }

  const pick = (business: BusinessSuggest) => {
    cancelPendingLookup()
    setPicked(business)
    setText(business.label)
    setOptions([])
    emit({ businessId: business.id, employerName: undefined })
  }

  const commitFreeText = () => {
    if (picked) return
    cancelPendingLookup()
    setOptions([])
    const trimmed = text.trim()
    emit({ businessId: undefined, employerName: trimmed || undefined })
  }

  const clear = () => {
    cancelPendingLookup()
    setPicked(null)
    setText('')
    setOptions([])
    emit({ businessId: undefined, employerName: undefined })
  }

  return (
    <Box position="relative" minW="240px" flex={1}>
      <HStack gap={2}>
        <Input
          aria-label="Employer"
          size="sm"
          minH="44px"
          placeholder={placeholder}
          value={text}
          onChange={(e) => { typingRef.current = true; setPicked(null); setText(e.target.value) }}
          onBlur={commitFreeText}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitFreeText() } }}
        />
        {(picked || value.businessId || value.employerName) && (
          <Button size="sm" minH="44px" variant="ghost" onClick={clear}>Clear</Button>
        )}
      </HStack>
      {picked && (
        <Text fontSize="xs" color="green.700" mt={1}>
          Matching the linked employer "{picked.label}".
        </Text>
      )}
      {!picked && options.length > 0 && (
        <VStack
          align="stretch" gap={0} mt={1} bg="surface" borderWidth="1px" borderColor="gray.200"
          borderRadius="md" shadow="md" position="absolute" zIndex={20} w="full"
          maxH="240px" overflowY="auto">
          {options.map((o) => (
            <Button
              key={o.id} variant="ghost" justifyContent="flex-start" minH="44px"
              borderRadius={0}
              // Pick on mousedown, not just onClick: mousedown fires before blur, so preventing
              // the default focus shift here (and picking right away) wins the race against
              // commitFreeText's onBlur — a bare fireEvent.click with no prior mousedown (the
              // existing suite) still lands via onClick below.
              onMouseDown={(e) => { e.preventDefault(); pick(o) }}
              onClick={() => pick(o)}>
              {o.label}
              <Text as="span" color="gray.500" fontSize="xs" ml={2}>
                {o.worksiteCount} worksite{o.worksiteCount === 1 ? '' : 's'}
              </Text>
            </Button>
          ))}
        </VStack>
      )}
    </Box>
  )
}
